import { AlertDescription, AlertTitle } from '@/components/ui/alert.js';
import { Alert, Button } from '@/components/ui/index.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import interactAgentTaskEvents from '@/constants/interactAgentTaskEvents.js';
import { useKayAuth } from '@/context/AuthContext.js';
import backendApis from '@/services/backendApis.js';
import {
  CarrierStateInfo,
  CarrierStatus,
  CarrierVerification,
  LiveLink,
} from '@/types/carrier.js';
import { useAuth } from '@clerk/clerk-react';
import { CircleAlert } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

export type BrowserbaseRendererProps = {
  state: CarrierStateInfo;
  verification: CarrierVerification;
};

const BrowserbaseRenderer = (props: BrowserbaseRendererProps) => {
  const { state, verification } = props;
  const { user } = useKayAuth();
  const { getToken } = useAuth();
  const userId = user?.user_id;
  const sessionId = state?.session_id;
  const [liveLinks, setLiveLinks] = useState<LiveLink[]>([]);
  const [selectedLiveLink, setSelectedLiveLink] = useState<LiveLink | null>(
    null,
  );
  const [restartLoading, setRestartLoading] = useState(false);

  const checkSessionFailed = async () => {
    try {
      const params = {
        verification_id: sessionId,
      };
      const res = await backendApis.isCarrierAccessVerificationRunning(params);
      if (res.err) {
        throw new Error(res.err);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getLiveUrls = async () => {
    try {
      const params = {
        session_id: sessionId,
      };
      const res = await backendApis.getCarrierAccessLiveLinks(params);
      if (res.err) {
        throw new Error(res.err);
      }
      const liveLinks = res.data.live_links;
      setLiveLinks(liveLinks);
      if (liveLinks.length > 0 && !selectedLiveLink) {
        setSelectedLiveLink(liveLinks[0]);
      }
      setTimeout(() => {
        getLiveUrls();
      }, 15000);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    checkSessionFailed()
    getLiveUrls();
  }, [sessionId]);

  const restartFunc = async () => {
    setRestartLoading(true);
    try {
      const token = await getToken();
      const params = {
        job_context_id: verification.job_context_id,
        job_interaction: {
          action: interactAgentTaskEvents.restart,
          payload: {},
        },
        user_id: userId,
        workflow_run_id: verification.workflow_run_id,
      };
      const res = await backendApis.interact(params, token);
      if (res.err) {
        throw new Error(res.err);
      }
      toast.success('Carrier restarted successfully', {
        description:
          'Please wait for a few seconds for the new session to start',
      });
      setLiveLinks([]);
      setSelectedLiveLink(null);
      getLiveUrls();
    } catch (e) {
      console.error(e);
    } finally {
      setRestartLoading(false);
    }
  };

  if (!sessionId) {
    return (
      <div>
        <h1>Connect URL is required</h1>
      </div>
    );
  }

  if (state.carrier_status === CarrierStatus.Failed) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center px-6 py-10 text-center">
        <div className="mb-6 flex flex-col items-center gap-2">
          <div className="rounded-full bg-red-100 p-3">
            <svg
              className="h-6 w-6 text-red-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold">Session Expired</h1>
          <p className="text-muted-foreground max-w-md text-sm">
            We couldn’t access the carrier portal automatically. Your login
            session may have expired.
          </p>
        </div>

        <div className="w-full max-w-md rounded-lg border bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-start gap-2">
            <svg
              className="mt-1 h-5 w-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            ></svg>
            <div className="text-left">
              <h2 className="font-medium">
                To continue, please log in manually
              </h2>
              <p className="text-muted-foreground text-sm">
                We’ll guide you through the steps and resume the task once
                you’re in.
              </p>
            </div>
          </div>
          <Button
            className={`w-full ${restartLoading ? 'bg-gray-300' : ''}`}
            disabled={restartLoading}
            onClick={restartFunc}
          >
            {restartLoading ? 'Restarting...' : 'Login & Continue'}
          </Button>
        </div>
      </div>
    );
  }

  if (state.carrier_status !== CarrierStatus.Pending) {
    return (
      <div className="flex h-full flex-col items-center justify-center space-y-4 px-4 text-center">
        <div className="border-t-primary h-10 w-10 animate-spin rounded-full border-4 border-gray-300" />
        <div>
          <h1 className="text-lg font-semibold">Verifying Carrier Access</h1>
          <p className="text-muted-foreground max-w-sm text-sm">
            We’re currently verifying connection to the carrier portal. This may
            take a few seconds.
          </p>
        </div>
      </div>
    );
  }

  if (sessionId && !selectedLiveLink && liveLinks.length === 0) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-w-full space-y-3">
      <Alert>
        <AlertTitle>
          <div className="flex items-center gap-2">
            <CircleAlert className="text-red-500" />
            <p className="font-bold text-red-500">Session Expired</p>
          </div>
        </AlertTitle>
        <AlertDescription className='flex flex-col'>
          <span>
            We couldn’t access the carrier portal automatically. Your login
            session may have expired.
          </span>
          <span>
            To Continue, Please log in manually. We’ll resume the task once you’re in.
          </span>
          <span className='font-bold text-red-500'>
            If you have changed the password, please update it in the settings.
          </span>
        </AlertDescription>
      </Alert>

      <div className="flex justify-between">
        <span className="font-bold capitalize">{state?.name}</span>

        <Select
          onValueChange={(value) =>
            setSelectedLiveLink(
              liveLinks.find((liveLink) => liveLink.id === value),
            )
          }
          value={selectedLiveLink?.id}
        >
          <SelectTrigger className="w-[250px] bg-white">
            <SelectValue placeholder="Select Link">
              {selectedLiveLink?.title}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {liveLinks.map((liveLink) => (
              <SelectItem key={liveLink.id} value={liveLink.id}>
                {liveLink.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <iframe
        allow="clipboard-read; clipboard-write"
        className="min-h-[80vh] min-w-full"
        sandbox="allow-same-origin allow-scripts"
        src={selectedLiveLink?.debugger_fullscreen_url}
      />
    </div>
  );
};

export default BrowserbaseRenderer;
